import { create } from 'zustand';
import { api } from '@/lib/api';
import { isLyrraOne } from '@/lib/platform';
import { applyAppLanguage } from '@/lib/apply-app-language';
import { storeVisitorCountryCode } from '@/lib/visitor-geo-locale';
import { hasUserPersistedLanguage, type Language } from '@/lib/language-prefs';
import {
  getMarketConfig,
  type LyrraOnePublicRegion,
} from '@/lib/lyrraone-market-config';
import {
  countryToLyrraOneRegion,
  detectRegionFromTimezone,
  getActiveRegionFromSearch,
  readPreviewRegionFromSearch,
  readRegionFromSearch,
  readStoredLyrraOneRegion,
  storeLyrraOneRegion,
} from '@/lib/lyrraone-public-region';

interface PublicRegionState {
  region: LyrraOnePublicRegion;
  initialized: boolean;
  urlOverride: boolean;
  setRegion: (region: LyrraOnePublicRegion) => void;
  applyFromSearch: (search: string) => void;
  initialize: () => Promise<void>;
}

function syncLanguageStore(partial: { language?: Language; visitorCountryCode?: string | null }): void {
  void import('@/stores/language.store').then(({ useLanguageStore }) => {
    useLanguageStore.setState(partial);
  });
}

function applyRegionSideEffects(region: LyrraOnePublicRegion, opts?: { persistLanguage?: boolean }): void {
  const market = getMarketConfig(region);
  const countryCode = market.countryCode === 'INTL' ? null : market.countryCode;

  if (countryCode) {
    storeVisitorCountryCode(countryCode);
    syncLanguageStore({ visitorCountryCode: countryCode });
  }

  if (!hasUserPersistedLanguage()) {
    applyAppLanguage(market.defaultLanguage, { persist: opts?.persistLanguage ?? false });
    syncLanguageStore({ language: market.defaultLanguage });
  }
}

function resolveInitialRegion(): LyrraOnePublicRegion {
  if (typeof window === 'undefined') return 'intl';

  const fromSearch = getActiveRegionFromSearch(window.location.search);
  if (fromSearch) {
    const preview = readPreviewRegionFromSearch(window.location.search);
    if (!preview) storeLyrraOneRegion(fromSearch);
    return fromSearch;
  }

  const stored = readStoredLyrraOneRegion();
  if (stored) return stored;

  return detectRegionFromTimezone();
}

const initialRegion = resolveInitialRegion();

export const usePublicRegionStore = create<PublicRegionState>((set, get) => ({
  region: initialRegion,
  initialized: false,
  urlOverride: typeof window !== 'undefined' && getActiveRegionFromSearch(window.location.search) !== null,

  setRegion: (region: LyrraOnePublicRegion) => {
    storeLyrraOneRegion(region);
    applyRegionSideEffects(region, { persistLanguage: true });
    set({ region, initialized: true, urlOverride: false });
  },

  applyFromSearch: (search: string) => {
    const preview = readPreviewRegionFromSearch(search);
    const persisted = readRegionFromSearch(search);
    const active = preview ?? persisted;

    if (active) {
      if (!preview) storeLyrraOneRegion(active);
      applyRegionSideEffects(active);
      set({ region: active, initialized: true, urlOverride: true });
      return;
    }

    set({ urlOverride: false });
    if (!get().initialized) {
      void get().initialize();
    }
  },

  initialize: async () => {
    if (!isLyrraOne()) return;
    if (get().initialized) return;

    const stored = readStoredLyrraOneRegion();
    if (stored) {
      applyRegionSideEffects(stored);
      set({ region: stored, initialized: true });
      return;
    }

    try {
      const res = await api.get<{ success?: boolean; data?: { country?: string | null } }>('/public/region-hint');
      const fromCountry = countryToLyrraOneRegion(res?.data?.country);
      if (fromCountry) {
        applyRegionSideEffects(fromCountry);
        set({ region: fromCountry, initialized: true });
        return;
      }
    } catch {
      /* géolocalisation optionnelle */
    }

    const fromTz = detectRegionFromTimezone();
    applyRegionSideEffects(fromTz);
    set({ region: fromTz, initialized: true });
  },
}));
